import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'

import { useBranchesQuery } from '../api/branches/api'
import { useGetMe } from '../api/user/api'
import { BranchInfo } from '../api/user/types'
import { useAuth } from '../auth-provider/auth-context'
import { UserContext } from './user-context'

const getBranchStorageKey = (userId: string) => `selected_branch_${userId}`

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { logOut } = useAuth()
  const { data: user, error } = useGetMe({ nullWhenUnauthenticated: true })
  const { data: branches, isLoading: branchesLoading } = useBranchesQuery({ authorizedBranches: true })
  const [branchInfo, setBranchInfo] = useState<BranchInfo | null>(null)
  const branchInitialized = useRef(false)

  // sync branchId to some branch that the user has, checking localStorage first
  useEffect(() => {
    const branchInfos: BranchInfo[] =
      branches?.items.map((b) => ({
        provider_branch_id: b.id,
        metro_area: b.metro_area ?? null,
        labor_rate: b.labor_rate ?? null,
        stripe_account_id: b.stripe_account_id ?? null,
      })) ?? []
    if (user === undefined || user === null) {
      setBranchInfo(null)
      branchInitialized.current = false
    } else if (!branchInitialized.current && branchInfos.length > 0) {
      const savedBranchId = localStorage.getItem(getBranchStorageKey(user.id))
      // Only use the saved branch if it's in the user's available branches
      if (savedBranchId && branchInfos.some((b) => b.provider_branch_id === savedBranchId)) {
        setBranchInfo(branchInfos.find((b) => b.provider_branch_id === savedBranchId) ?? null)
      } else {
        setBranchInfo(branchInfos[0])
        localStorage.setItem(getBranchStorageKey(user.id), branchInfos[0].provider_branch_id)
      }
      // Don't try to access branchInfo here right after setting it
      branchInitialized.current = true
    }
  }, [user, branches])

  // Add a separate effect to log when branchInfo changes

  useEffect(() => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      logOut()
    }
  }, [error, logOut])

  return (
    <UserContext.Provider
      value={{
        user: user ?? null,
        isAdmin: (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') ?? null,
        role: user?.role ?? null,
        branchesLoading,
        branchInfo,
        selectBranch: (branchInfo: BranchInfo) => {
          setBranchInfo(branchInfo)
          if (user) {
            localStorage.setItem(getBranchStorageKey(user.id), branchInfo.provider_branch_id)
          }
        },
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserProvider
